import { colors } from "./colors";
import { borderRadiusPx } from "./borderRadius";
import { customShadows } from "./shadows";

/**
 * Material UI Component Overrides & Default Props for Autobet
 */
export const createComponentOverrides = () => ({
  MuiCssBaseline: {
    styleOverrides: {
      "*": {
        boxSizing: "border-box",
        margin: 0,
        padding: 0,
      },
      html: {
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        scrollbarGutter: "stable",
        overflowX: "hidden",
      },
      body: {
        color: colors.secondary.main,
        backgroundColor: "transparent",
        overflowX: "hidden",
        "&[style*='padding-right']": {
          paddingRight: "0px !important",
        },
      },
      "body.modal-open, body[style*='padding-right']": {
        paddingRight: "0px !important",
      },
    },
  },

  MuiModal: {
    defaultProps: {
      disableScrollLock: true,
    },
  },

  MuiPopover: {
    defaultProps: {
      disableScrollLock: true,
    },
  },

  MuiDrawer: {
    defaultProps: {
      disableScrollLock: true,
    },
  },

  MuiSelect: {
    defaultProps: {
      MenuProps: {
        disableScrollLock: true,
      },
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        borderRadius: borderRadiusPx.md,
        fontWeight: 600,
        textTransform: "none",
        padding: "8px 20px",
        transition: "all 0.25s ease-in-out",
        "&:hover": {
          transform: "translateY(-1px)",
        },
      },
      containedPrimary: {
        backgroundColor: colors.primary.main,
        color: colors.primary.contrastText,
        boxShadow: customShadows.primaryGlowSmall,
        "&:hover": {
          backgroundColor: colors.primary.dark,
          boxShadow: customShadows.primaryGlow,
        },
      },
      containedSecondary: {
        backgroundColor: colors.secondary.main,
        color: colors.secondary.contrastText,
        "&:hover": {
          backgroundColor: colors.secondary.light,
          boxShadow: customShadows.secondaryGlow,
        },
      },
      outlinedPrimary: {
        borderColor: colors.primary.main,
        color: colors.primary.main,
        "&:hover": {
          borderColor: colors.primary.dark,
          backgroundColor: colors.primary.subtle,
        },
      },
      outlinedSecondary: {
        borderColor: colors.secondary.main,
        color: colors.secondary.main,
        "&:hover": {
          borderColor: colors.secondary.light,
          backgroundColor: "rgba(0, 42, 51, 0.04)",
        },
      },
      sizeLarge: {
        padding: "12px 28px",
        fontSize: "1rem",
        borderRadius: borderRadiusPx.lg,
      },
      sizeSmall: {
        padding: "4px 12px",
        fontSize: "0.8125rem",
        borderRadius: borderRadiusPx.sm,
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: borderRadiusPx.default,
        boxShadow: customShadows.card,
        border: `1px solid ${colors.neutral.divider}`,
        backgroundColor: colors.background.paper,
        transition: "box-shadow 0.25s ease-in-out, transform 0.25s ease-in-out",
        "&:hover": {
          boxShadow: customShadows.md,
        },
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
      rounded: {
        borderRadius: borderRadiusPx.default,
      },
      elevation1: {
        boxShadow: customShadows.sm,
      },
      elevation2: {
        boxShadow: customShadows.md,
      },
      elevation3: {
        boxShadow: customShadows.lg,
      },
      elevation4: {
        boxShadow: customShadows.xl,
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: borderRadiusPx.default,
        backgroundColor: colors.background.paper,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.neutral.border,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.primary.main,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.primary.main,
          borderWidth: "2px",
        },
      },
      input: {
        padding: "12px 14px",
      },
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: "outlined",
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        borderRadius: borderRadiusPx.xxl,
      },
      colorPrimary: {
        backgroundColor: colors.primary.lighter,
        color: colors.primary.dark,
      },
      colorSecondary: {
        backgroundColor: colors.secondary.lighter,
        color: colors.secondary.main,
      },
    },
  },

  MuiDialog: {
    defaultProps: {
      disableScrollLock: true,
    },
    styleOverrides: {
      root: {
        zIndex: "1000000 !important",
      },
      paper: {
        borderRadius: borderRadiusPx.lg,
        boxShadow: customShadows.modal,
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: "'Poppins', sans-serif",
        fontWeight: 600,
        color: colors.secondary.main,
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: colors.secondary.main,
        borderRadius: borderRadiusPx.sm,
        fontSize: "0.75rem",
        boxShadow: customShadows.sm,
      },
    },
  },

  MuiMenu: {
    defaultProps: {
      disableScrollLock: true,
    },
    styleOverrides: {
      root: {
        zIndex: "10000005 !important",
      },
      paper: {
        borderRadius: borderRadiusPx.md,
        boxShadow: customShadows.dropdown,
      },
    },
  },

  MuiPopover: {
    defaultProps: {
      disableScrollLock: true,
    },
    styleOverrides: {
      root: {
        zIndex: "10000005 !important",
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: "0.875rem",
        padding: "10px 16px",
        "&.Mui-selected": {
          backgroundColor: colors.primary.subtle,
          color: colors.primary.dark,
          fontWeight: 600,
        },
        "&:hover": {
          backgroundColor: colors.action.hover,
        },
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: customShadows.xs,
        backgroundColor: colors.background.paper,
        color: colors.secondary.main,
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        textTransform: "none",
        fontSize: "0.9375rem",
        "&.Mui-selected": {
          color: colors.primary.main,
        },
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: colors.primary.main,
        height: 3,
        borderRadius: "3px 3px 0 0",
      },
    },
  },
});

export default createComponentOverrides;
